import { ALISHA_PHOTO, BRANDON_MEG_PHOTO } from '@/components/agent-apply/le-miami-photos';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const EASE = [0.22, 1, 0.36, 1] as const;

interface WhoWeWantProps {
    onApply: () => void;
}

export default function WhoWeWant({ onApply }: WhoWeWantProps) {
    const shouldReduceMotion = useReducedMotion();

    return (
        <section id="who" className="agent-px scroll-mt-36 bg-[#0b2222] py-20 sm:py-28">
            <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[20rem_minmax(0,1fr)] lg:items-start lg:gap-16">
                {/* Tall portrait — same editorial grade + caption treatment as the hero image.
                    Hidden on mobile (it's paired beside the hero photo up top there); shown on desktop. */}
                <motion.figure
                    className="mx-auto hidden w-full max-w-sm lg:mx-0 lg:block lg:max-w-none"
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.8, ease: EASE }}
                >
                    <img
                        src={BRANDON_MEG_PHOTO.src}
                        alt={BRANDON_MEG_PHOTO.alt}
                        className="editorial-filter w-full rounded-xl object-cover shadow-2xl ring-1 ring-[#F5F5F5]/10"
                    />
                    <figcaption className="mt-4 text-[11px] leading-[1.7] tracking-[0.2em] text-[#F5F5F5]/40 uppercase">
                        {BRANDON_MEG_PHOTO.caption}
                    </figcaption>
                </motion.figure>

                <motion.div
                    className="max-w-3xl"
                    initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: EASE }}
                >
                    <span className="lux-caps lux-caps-lg mb-4 block text-[#F5F5F5]/60">New &amp; Experienced Agents Welcome</span>
                    <h2 className="lux-didot lux-didot-lg mb-6 text-[#F5F5F5]">Who We&rsquo;re Looking For</h2>

                    <div className="space-y-4">
                        <p className="lux-body lux-body-lg leading-relaxed text-[#F5F5F5]/75">
                            We don&rsquo;t hire on years in travel — we hire on earning potential, network strength, and adaptability. Some of our
                            highest producers started with zero travel background but brought established networks and the discipline to build a real
                            business.
                        </p>
                        <p className="lux-body lux-body-lg leading-relaxed text-[#F5F5F5]/75">
                            Your network matters most. If you don&rsquo;t know many people who travel, be ready to meet new ones. Two profiles tend to
                            thrive here:
                        </p>
                    </div>

                    <div className="mt-10 space-y-10">
                        {/* Profile 1 */}
                        <div>
                            <h3 className="lux-body lux-body-sm mb-3 font-medium text-[#F5F5F5]/90">Emerging Advisors with Established Networks</h3>
                            <div className="space-y-3">
                                <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/60">
                                    You may be new to travel, but you&rsquo;re not new to business. You have a strong personal or professional network —
                                    colleagues, executives, founders, families, or communities that already trust you. You&rsquo;re a quick learner,
                                    comfortable with technology, and ready to monetize relationships the right way.
                                </p>
                                <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/60">
                                    Travel can be a powerful extension of an existing network — when paired with the right infrastructure.
                                </p>
                            </div>
                        </div>

                        {/* Profile 2 */}
                        <div>
                            <h3 className="lux-body lux-body-sm mb-3 font-medium text-[#F5F5F5]/90">Experienced Advisors Ready to Evolve</h3>
                            <div className="space-y-3">
                                <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/60">
                                    You&rsquo;ve been in the industry and know how to close. You&rsquo;re not looking for training — you&rsquo;re looking
                                    for better systems, stronger contracts, and higher yield per booking. You&rsquo;re open to new tools, new workflows,
                                    and more efficient ways to operate.
                                </p>
                                <p className="lux-body lux-body-sm leading-relaxed text-[#F5F5F5]/60">
                                    We invest heavily in proprietary infrastructure. Advisors who thrive here are willing to adopt it.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <motion.div
                        className="mt-12"
                        initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2, ease: EASE }}
                    >
                        <button
                            type="button"
                            onClick={onApply}
                            className="lux-caps lux-caps-lg group inline-flex items-center gap-2 text-[#F5F5F5]/60 transition-colors hover:text-[#F5F5F5]"
                        >
                            Sound like you? Apply now
                            <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:translate-y-0.5" />
                        </button>
                    </motion.div>

                    {/* LE Miami photos — mobile only, side by side at the bottom of the section (above How It Works).
                        On desktop these live elsewhere (Alisha in the hero, Brandon/Meg in the portrait column). */}
                    <div className="mt-12 flex items-start gap-3 lg:hidden">
                        <figure className="min-w-0 flex-1">
                            <img
                                src={ALISHA_PHOTO.src}
                                alt={ALISHA_PHOTO.alt}
                                className="editorial-filter w-full rounded-xl object-cover shadow-2xl ring-1 ring-[#F5F5F5]/10"
                            />
                            <figcaption className="mt-4 text-[11px] leading-[1.7] tracking-[0.2em] text-[#F5F5F5]/40 uppercase">
                                {ALISHA_PHOTO.caption}
                            </figcaption>
                        </figure>
                        <figure className="min-w-0 flex-1">
                            <img
                                src={BRANDON_MEG_PHOTO.src}
                                alt={BRANDON_MEG_PHOTO.alt}
                                className="editorial-filter w-full rounded-xl object-cover shadow-2xl ring-1 ring-[#F5F5F5]/10"
                            />
                            <figcaption className="mt-4 text-[11px] leading-[1.7] tracking-[0.2em] text-[#F5F5F5]/40 uppercase">
                                {BRANDON_MEG_PHOTO.caption}
                            </figcaption>
                        </figure>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
